import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api/client'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const accessToken = ref(localStorage.getItem('access_token'))
  const refreshToken = ref(localStorage.getItem('refresh_token'))
  const initialized = ref(false)
  const loading = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const isInstructor = computed(() => user.value?.role === 'instructor' || user.value?.role === 'admin')
  const isAdmin = computed(() => user.value?.role === 'admin')

  // Actions
  async function login(credentials) {
    loading.value = true
    try {
      const response = await api.post('/api/v1/auth/token/', credentials)
      setTokens(response.data.access, response.data.refresh)
      await fetchUser()
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Error al iniciar sesion'
      }
    } finally {
      loading.value = false
    }
  }

  async function register(userData) {
    loading.value = true
    try {
      const response = await api.post('/api/v1/auth/register/', userData)
      if (response.data.tokens) {
        setTokens(response.data.tokens.access, response.data.tokens.refresh)
        user.value = response.data.user
      }
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Error al registrarse'
      }
    } finally {
      loading.value = false
    }
  }

  async function fetchUser() {
    if (!accessToken.value) return null

    try {
      const response = await api.get('/api/v1/auth/users/me/')
      user.value = response.data.data
      return user.value
    } catch (error) {
      if (error.response?.status === 401) {
        await refreshAccessToken()
      }
      return null
    }
  }

  async function refreshAccessToken() {
    if (!refreshToken.value) {
      logout()
      return false
    }

    try {
      const response = await api.post('/api/v1/auth/token/refresh/', {
        refresh: refreshToken.value
      })
      setTokens(response.data.access, response.data.refresh || refreshToken.value)
      return true
    } catch (error) {
      logout()
      return false
    }
  }

  async function checkAuth() {
    if (initialized.value) return

    if (accessToken.value) {
      await fetchUser()
    }
    initialized.value = true
  }

  function setTokens(access, refresh) {
    accessToken.value = access
    refreshToken.value = refresh
    localStorage.setItem('access_token', access)
    if (refresh) {
      localStorage.setItem('refresh_token', refresh)
    }
    api.defaults.headers.common['Authorization'] = `Bearer ${access}`
  }

  function logout() {
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    delete api.defaults.headers.common['Authorization']
  }

  async function updateProfile(profileData) {
    loading.value = true
    try {
      const response = await api.patch('/api/v1/auth/users/me/', profileData)
      user.value = response.data.data
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Error al actualizar perfil'
      }
    } finally {
      loading.value = false
    }
  }

  // Initialize token in headers if exists
  if (accessToken.value) {
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken.value}`
  }

  return {
    // State
    user,
    accessToken,
    refreshToken,
    initialized,
    loading,
    // Getters
    isAuthenticated,
    isInstructor,
    isAdmin,
    // Actions
    login,
    register,
    logout,
    fetchUser,
    checkAuth,
    refreshAccessToken,
    updateProfile
  }
})
